import React, { useState, useEffect, useCallback } from "react";
import { getPendingPosts, approveRejectPost } from "../../../../api/ApiConfig";
import "./hr.css"; // Assuming this CSS file exists
import { FaRegFolderOpen } from "react-icons/fa";
import toast from "react-hot-toast";

const ApproveRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");
  const [showRemarkBox, setShowRemarkBox] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 5,
    has_next: false,
    total_pages: 1,
    total_items: 0,
  });

  // Extracted data fetching logic into a reusable, memoized function
  const fetchPendingPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPendingPosts({
        page: pagination.page,
        page_size: pagination.page_size,
      });

      if (response.status === "success") {
        // Ensure index starts from 1 for the current page
        const baseSlno = (response.data.page - 1) * response.data.page_size;

        const transformedData = response.data.items.map((item, index) => ({
          slno: baseSlno + index + 1,
          uploadedBy: item.emp_name,
          employeeId: item.emp_id,
          postType: item.posts_type,
          // Format date as locale string
          dateTime: new Date(item.created_on).toLocaleDateString(),
          remarks: "",
          // Ensure media is an array even if posts_attachments is null/undefined
          media: item.posts_attachments || [],
          description: item.posts_contents,
          originalData: item,
        }));

        setRequests(transformedData);
        setPagination({
          page: response.data.page,
          page_size: response.data.page_size,
          has_next: response.data.has_next,
          total_pages: response.data.total_pages,
          total_items: response.data.total_items,
        });
      } else {
        setError(response.message || "Failed to fetch pending posts");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size]); // Depend on pagination state

  // useEffect runs whenever page or page_size changes
  useEffect(() => {
    fetchPendingPosts();
  }, [fetchPendingPosts]);

  const handleView = (request) => {
    setSelectedPost(request);
    setRejectRemark("");
    setShowRemarkBox(false);
    // Reset media index to 0 when viewing a new post
    setMediaIndex(0); 
  };

  const handleClose = () => {
    setSelectedPost(null);
    setRejectRemark("");
    setShowRemarkBox(false);
  };

  const handleApprove = async () => {
    try {
      if (!selectedPost) return;

      const postId = selectedPost.originalData.posts_id;
      const response = await approveRejectPost({
        posts_id: postId,
        action: "approved",
      });

      if (response.status === "success") {
        toast.success("Post approved successfully");
        // Re-fetch the current page to update the list
        await fetchPendingPosts(); 
        handleClose();
      } else {
        toast.error(`Approval failed: ${response.message}`);
      }
    } catch (error) {
      toast.error(`Error approving post: ${error.message}`);
    }
  };

  const handleReject = async () => {
    if (!showRemarkBox) {
      // First click: show the remark box
      setShowRemarkBox(true);
      return;
    }

    // Second click (or after remark box is visible): process rejection
    if (!rejectRemark.trim()) {
      toast.error("Please enter a remark for rejection.");
      return;
    }

    try {
      if (!selectedPost) return;

      const postId = selectedPost.originalData.posts_id;
      const response = await approveRejectPost({
        posts_id: postId,
        action: "rejected",
        reject_reason: rejectRemark,
      });

      if (response.status === "success") {
        toast.success(`Post rejected successfully: ${selectedPost.uploadedBy}`);
        // Re-fetch the current page to update the list
        await fetchPendingPosts(); 
        handleClose();
      } else {
        toast.error(`Rejection failed: ${response.message}`);
      }
    } catch (error) {
      toast.error(`Error rejecting post: ${error.message}`);
    }
  };

  const goNext = () => {
    if (selectedPost && selectedPost.media.length > 0) {
      setMediaIndex((prev) =>
        prev + 1 < selectedPost.media.length ? prev + 1 : 0
      );
    }
  };

  const goPrev = () => {
    if (selectedPost && selectedPost.media.length > 0) {
      setMediaIndex((prev) =>
        prev - 1 >= 0 ? prev - 1 : selectedPost.media.length - 1
      );
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setPagination((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination((prev) => ({
        ...prev,
        page: prev.page - 1,
      }));
    }
  };

  if (loading) {
    return <div className="container center-text">Loading...</div>;
  }

  if (error) {
    return <div className="container center-text error-message">Error: {error}</div>;
  }
  
  // Helper variable for cleaner modal media rendering
  const currentMedia = selectedPost?.media?.[mediaIndex];
  const mediaAvailable = selectedPost?.media?.length > 0 && currentMedia && currentMedia.content;

  return (
    <div className="container">
      <h3 className="center">Approve Requests</h3>
      {requests.length === 0 ? (
        <div className="no-posts">
          <div className="no-posts-icon">
            <FaRegFolderOpen />
          </div>
          <p>No Pending Posts Available</p>
        </div>
      ) : (
        <>
          <table className="request-table">
            <thead>
              <tr>
                <th>Slno</th>
                <th>Uploaded by</th>
                <th>Employee ID</th>
                <th>Post type</th>
                <th>Posted on</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req, idx) => (
                <tr key={idx}>
                  <td>{req.slno}</td>
                  <td>{req.uploadedBy}</td>
                  <td>{req.employeeId}</td>
                  <td>{req.postType}</td>
                  <td>{req.dateTime}</td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => handleView(req)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination controls */}
          <div className="pagination">
            <button
              onClick={handlePrevPage}
              disabled={pagination.page === 1}
              className="view-btn"
            >
              Prev
            </button>
            <p className="page-info">
              Page {pagination.page} of {pagination.total_pages}
            </p>
            <button
              onClick={handleNextPage}
              disabled={!pagination.has_next}
              className="view-btn"
            >
              Next
            </button>
          </div>

          {/* Post Details Modal */}
          {selectedPost && (
            <div className="modal-overlay mt-5">
              <div className="modal-content">
                <span className="modal-close-icon" onClick={handleClose}>
                  &times;
                </span>
                <h3 className="modal-heading">Post Details</h3>

                <div className="media-container">
                  {mediaAvailable && (
                    <button className="nav-button left" onClick={goPrev}>
                      ❮
                    </button>
                  )}
                  <div className="media-box">
                    {/* CRITICAL FIX: Check if currentMedia and its content exist */}
                    {mediaAvailable ? (
                      <>
                        {currentMedia.content.includes(".mp4") ? (
                          <video controls className="media">
                            <source
                              src={currentMedia.content}
                              type="video/mp4"
                            />
                          </video>
                        ) : (
                          <img
                            src={currentMedia.content}
                            alt="Post Media"
                            className="media"
                          />
                        )}
                        <span className="media-count">
                          {mediaIndex + 1}/{selectedPost.media.length}
                        </span>
                      </>
                    ) : (
                      <p className="no-media-text">No attachments found for this post.</p>
                    )}
                  </div>
                  {mediaAvailable && (
                    <button className="nav-button right" onClick={goNext}>
                      ❯
                    </button>
                  )}
                </div>

                <div className="post-info">
                  <div className="info-row">
                    <div className="info-group">
                      <label>Uploaded By</label>
                      <div className="info-field">
                        {selectedPost.uploadedBy}
                      </div>
                    </div>
                    <div className="info-group">
                      <label>Employee ID</label>
                      <div className="info-field">
                        {selectedPost.employeeId}
                      </div>
                    </div>
                  </div>

                  <div className="info-row">
                    <div className="info-group">
                      <label>Post Type</label>
                      <div className="info-field">{selectedPost.postType}</div>
                    </div>
                    <div className="info-group">
                      <label>Posted on</label>
                      <div className="info-field">{selectedPost.dateTime}</div>
                    </div>
                  </div>

                  <div className="info-group full-width">
                    <label>Post Content</label>
                    <textarea
                      className="info-textarea"
                      value={selectedPost.description}
                      readOnly
                    />
                  </div>
                </div>

                {showRemarkBox && (
                  <textarea
                    placeholder="Enter remarks if rejecting"
                    value={rejectRemark}
                    onChange={(e) => setRejectRemark(e.target.value)}
                    className="remark-box"
                  />
                )}

                <div className="modal-actions">
                  <button className="approve-btn" onClick={handleApprove}>
                    Approve
                  </button>
                  <button className="reject-btn" onClick={handleReject}>
                    {showRemarkBox ? "Confirm Rejection" : "Reject"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApproveRequests;